import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { routes } from "../Router";

interface AuthContextType {
  isAuthenticated: boolean;
  login: (token: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(
    !!localStorage.getItem("token"),
  );
  const navigate = useNavigate();

  const login = useCallback(
    (token: string) => {
      /**
       * Store the token in local storage and redirect to home page.
       */
      localStorage.setItem("token", token);
      setIsAuthenticated(true);
      navigate(routes.root, { replace: true });
    },
    [navigate],
  );

  const logout = useCallback(() => {
    /**
     * Remove the token from local storage and redirect to login page.
     */
    localStorage.removeItem("token");
    setIsAuthenticated(false);
    navigate(routes.login, { replace: true });
  }, [navigate]);

  useEffect(() => {
    /**
     * If the token is removed from local storage, redirect to login page.
     */
    const handleStorageChange = () => {
      if (!localStorage.getItem("token")) {
        setIsAuthenticated(false);
        navigate("/login", { replace: true });
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [navigate]);

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
